import React from 'react';
import { Points } from 'components/Points/Points';
import { AffordabilityResult, plural } from './affordability';

// The affordability sentence (Affordability Line block), one of six branches,
// with the three cheapest thumbnails; faded ones are cards the budget does not
// reach. Every point value is a <Points type="inline">.

const P: React.FC<{ v: number }> = ({ v }) => <Points type="inline" points={v} />;

const Sentence: React.FC<{ r: AffordabilityResult }> = ({ r }) => {
  switch (r.branch) {
    case 'allFit':
      return (
        <>
          That's enough to take <strong>every card in Recent Activity below</strong>, all {r.count} total <P v={r.total} />.
        </>
      );
    case 'someFit':
      return (
        <>
          That's enough to take <strong>{r.affordableCount} of the {r.count} cards</strong> in Recent Activity below. The rest would take another <P v={r.gap} />.
        </>
      );
    case 'oneFits':
      return (
        <>
          That's enough for <strong>{r.cardName}</strong> below, and most of the way to anything else on the page.
        </>
      );
    case 'noneFit':
      return (
        <>
          The cards below are big-ticket today, but the typical card traded this week goes for <P v={r.median} />. <P v={r.budget} /> covers about {r.typicalCount} of them.
        </>
      );
    case 'partClaimed':
      return (
        <>
          You have <P v={r.unclaimed} /> to claim now, enough for <strong>{r.affordableCount} of the {r.count} cards</strong> below. Finish setup for another <P v={r.remaining} />.
        </>
      );
    case 'emptyFeed':
      return (
        <>
          <P v={r.budget} /> covers about <strong>{r.typicalCount} typical {plural(r.typicalCount, 'card', 'cards')}</strong> traded on TradeMagic this week.
        </>
      );
    case 'noMedian':
    default:
      return (
        <>
          <P v={r.budget} /> is waiting for you.
        </>
      );
  }
};

interface Props {
  result: AffordabilityResult;
}

const AffordabilityLine: React.FC<Props> = ({ result }) => {
  const thumbs = result.sorted.slice(0, 3);
  return (
    <div className="setup-afford" data-testid="affordability-line" data-branch={result.branch}>
      {thumbs.length > 0 && (
        <div className="setup-afford__thumbs" aria-hidden="true">
          {thumbs.map((item, i) => (
            <img key={item.id} src={item.imageUrl} alt="" className={`setup-afford__thumb${i >= result.affordableCount ? ' setup-afford__thumb--faded' : ''}`} loading="lazy" />
          ))}
        </div>
      )}
      <p className="setup-afford__text">
        <Sentence r={result} />
      </p>
    </div>
  );
};

export default AffordabilityLine;
